import axios from 'axios';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchMe, getToken, loginUrl, logout as clearSession } from '../services/authService';
import type { AuthUser } from '../services/types';

/** Zustand der Authentifizierung im Frontend. */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

/** Wert des Auth-Contexts (siehe useAuth-Hook). */
export interface AuthContextValue {
  /** Aktueller Auth-Zustand. */
  readonly status: AuthStatus;
  /** Angemeldeter Benutzer (nur bei status === 'authenticated' gesetzt). */
  readonly user: AuthUser | null;
  /** Startet den Google-OAuth2-Login (Redirect zum Backend). */
  readonly login: () => void;
  /** Meldet den Benutzer lokal ab (JWT verwerfen). */
  readonly logout: () => void;
  /**
   * Laedt den Benutzer per GET /api/auth/me neu (z. B. nach dem
   * OAuth-Callback). Liefert true bei Erfolg. Bei HTTP 401 wird das
   * Token verworfen; bei anderen Fehlern (Netzwerk, 5xx) bleibt das
   * Token erhalten, damit ein erneuter Versuch ohne neuen Login
   * moeglich ist. In beiden Fehlerfaellen wird der Zustand auf
   * 'anonymous' gesetzt und false geliefert.
   */
  readonly refresh: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Stellt den Auth-Zustand fuer die gesamte Anwendung bereit.
 * Beim Mount wird ein vorhandenes Token per GET /api/auth/me validiert;
 * auf /auth/callback uebernimmt die Callback-Seite diese Validierung
 * selbst (verhindert einen doppelten /me-Request).
 */
export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  // Generationszaehler gegen Request-Races: Nur der neueste refresh()-
  // Aufruf darf den State setzen; logout() invalidiert laufende Requests.
  const requestGenerationRef = useRef<number>(0);

  const refresh = useCallback(async (): Promise<boolean> => {
    requestGenerationRef.current += 1;
    const generation = requestGenerationRef.current;
    try {
      const me = await fetchMe();
      if (generation !== requestGenerationRef.current) {
        // Veralteter Request (neuerer refresh() oder logout() dazwischen)
        return false;
      }
      setUser(me);
      setStatus('authenticated');
      return true;
    } catch (error) {
      if (generation !== requestGenerationRef.current) {
        return false;
      }
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Token ungueltig/abgelaufen -> Session verwerfen
        clearSession();
      } else {
        // Netzwerk-/Serverfehler: Token BEHALTEN, damit ein erneuter
        // Versuch (z. B. Reload) ohne neuen Login moeglich bleibt
        console.error('Benutzer konnte nicht geladen werden:', error);
      }
      setUser(null);
      setStatus('anonymous');
      return false;
    }
  }, []);

  const login = useCallback((): void => {
    window.location.assign(loginUrl());
  }, []);

  const logout = useCallback((): void => {
    // Laufende refresh()-Requests invalidieren, damit eine spaete
    // Antwort den Logout nicht wieder ueberschreibt
    requestGenerationRef.current += 1;
    clearSession();
    setUser(null);
    setStatus('anonymous');
  }, []);

  // Mount: vorhandenes Token validieren, sonst direkt anonym.
  // Auf /auth/callback ruft die Callback-Seite refresh() selbst auf,
  // daher wird die Validierung hier uebersprungen (kein doppelter
  // /me-Request, keine Race zwischen zwei parallelen refresh-Aufrufen).
  useEffect(() => {
    if (window.location.pathname === '/auth/callback') {
      return;
    }
    if (getToken() === null) {
      setStatus('anonymous');
      return;
    }
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout, refresh }),
    [status, user, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
